/**
 * Fisco sul telefono (7/10/2026): risponde a "quali tasse dobbiamo pagare, e quando?".
 *
 * Prima, a 390px: una frase di sette righe, poi schede alte mezzo schermo con
 * quattro etichette maiuscole (DA QUANTO È SCADUTA / IMPORTO DA VERSARE / STATO
 * DEL VERSAMENTO / DA DOVE VIENE LA CIFRA) e i bottoni: si vedeva una tassa e
 * mezza per volta, e il calendario non sembrava un calendario.
 * Qui la forma è un CALENDARIO:
 *   1. in grande, quanto non è stato versato in tempo (o, se è tutto in
 *      regola, il prossimo versamento);
 *   2. le viste: calendario, F24 dei dipendenti, IVA, IRES e IRAP, debiti a
 *      rate, altri versamenti;
 *   3. calendario: prima le tasse non versate in tempo, poi un gruppo per mese
 *      con il giorno a sinistra di ogni riga; in fondo quelle già pagate
 *      nell'anno, una riga per tipo, che porta alla sua vista;
 *   4. le viste per tipo: due o tre numeri in breve, le tasse da pagare, le
 *      pagate (le prime cinque, le altre a richiesta).
 * Si tocca una tassa e sale il pannello con i gesti di prima: conferma il
 * pagamento trovato in banca, segna pagata, inserisci la cifra, modifica,
 * elimina. Aggiungere una tassa nuova resta al computer.
 * Stesse chiamate e stessa cache del computer (/admin/fisco/sintesi e
 * /admin/fisco/gruppo/:gruppo), stesso `?vista=`.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TAX_GROUP_LABEL, TAX_SOURCE_LABEL, TAX_SOURCES_PROVVISORIE, type TaxGroup } from '@masamune/shared';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { Icon } from '../../../components/ui/Icon';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow, MSeg } from '../../../components/mobile/MobileKit';
import { fmtEur } from '../../../lib/utils';
import { FISCO_VISTE, adminHref, type FiscoVista } from '../../../lib/adminRoutes';
import { canWriteAdmin, plurale } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { SettlePopover } from '../SettlePopover';
import { AdminHeader, useVista } from '../shared/AdminShell';
import { DueRighe, Fatto, Foglio, Gesto, Testo, VisteInVista, aGruppi, euro, euroEsatto, euroRiga, giornoBreve, meseGruppo } from '../shared/AdminMobile';
import {
  VISTA_DI_GRUPPO, fiscoGruppoQuery, fiscoSintesiQuery, useConfermaPossibile,
  type PianoRate, type RigaFisco, type Sintesi, type StatoVersamento, type VistaGruppo, type VoceFascia,
} from './fiscoApi';

const STATO_LABEL: Record<StatoVersamento, string> = {
  NON_VERSATA: 'Non versata in tempo',
  A_RATE: 'A rate',
  DA_PAGARE: 'Da pagare',
  PAGATA: 'Pagata',
  NEL_PIANO: 'Nel piano a rate',
};

/** Le pillole: corte, la barra in alto dice già "Fisco". */
const VISTA_CORTA: Record<FiscoVista, string> = {
  scadenze: 'Calendario',
  f24: 'F24 dipendenti',
  iva: 'IVA',
  imposte: 'IRES e IRAP',
  rate: 'Debiti a rate',
  altri: 'Altri versamenti',
};

const GRUPPO_DI_VISTA: Partial<Record<FiscoVista, TaxGroup>> = {
  f24: 'F24_DIPENDENTI', iva: 'IVA', imposte: 'SOCIETA', rate: 'RATE', altri: 'ALTRI',
};

const PRIME = 5;

/** La cifra è una stima o un nostro calcolo: si dice sotto l'importo, piccolo. */
const provvisoria = (r: RigaFisco) => (TAX_SOURCES_PROVVISORIE.includes(r.fonte) ? (TAX_SOURCE_LABEL[r.fonte] ?? '').toLowerCase() : undefined);

export function FiscoMobile() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [vista, setVista] = useVista<FiscoVista>(FISCO_VISTE);
  const { data: s } = useQuery(fiscoSintesiQuery);
  const [sel, setSel] = useState<RigaFisco | null>(null);
  const [paga, setPaga] = useState<RigaFisco | null>(null);
  const { pay } = useAdminActions();

  const vai = (g: TaxGroup) => {
    setVista(VISTA_DI_GRUPPO[g]);
    document.querySelector('.os-main')?.scrollTo?.({ top: 0, behavior: 'smooth' });
  };
  // Una riga che riassume un'altra vista (l'IVA di nove trimestri) porta lì e basta.
  const apri = (r: RigaFisco) => {
    if (r.vai && !r.installmentId && r.stato !== 'NEL_PIANO') vai(r.vai);
    else setSel(r);
  };

  const viste = (
    <VisteInVista chiave={vista}>
      <MSeg<FiscoVista>
        value={vista}
        onChange={setVista}
        options={FISCO_VISTE.map((k) => ({ key: k, label: VISTA_CORTA[k] }))}
      />
    </VisteInVista>
  );
  const gruppo = GRUPPO_DI_VISTA[vista];

  return (
    <div className="page adm-page">
      <AdminHeader title="Fisco" />
      <MPage>
        {s && <Risposta s={s} />}
        {viste}
        {vista === 'scadenze' && <Calendario s={s} onApri={apri} onVai={vai} />}
        {gruppo && <VistaTipo group={gruppo} canWrite={canWrite} onApri={apri} />}
      </MPage>

      {sel && (
        <FoglioTassa r={sel} canWrite={canWrite} onClose={() => setSel(null)} onVai={(g) => { setSel(null); vai(g); }}
          onPaga={(r) => { setSel(null); setPaga(r); }} />
      )}
      {paga && paga.installmentId && paga.importo != null && (
        <SettlePopover
          mode="paga"
          amount={paga.importo}
          target={{ kind: 'TAX_INSTALLMENT', id: paga.installmentId }}
          counterparty={paga.nome}
          side="DEBIT"
          pending={pay.isPending}
          onClose={() => setPaga(null)}
          onConfirm={(v) => {
            const r = paga;
            setPaga(null);
            pay.mutate({ source: 'TAX', id: r.installmentId!, amount: r.importo!, label: r.nome, ...v });
          }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La risposta
// ════════════════════════════════════════════════════════════════════

function Risposta({ s }: { s: Sintesi }) {
  const indietro = s.gruppi.find((g) => g.key === 'indietro');
  if (indietro && indietro.righe.length > 0) {
    const vecchia = indietro.righe[0]!;
    return (
      <MHero
        label="Tasse non versate in tempo"
        unit="€"
        value={fmtEur(indietro.totale)}
        alert
        sub={<>
          {plurale(indietro.righe.length, 'versamento', 'versamenti')}, il più vecchio {vecchia.quando}
          {indietro.senzaCifra > 0 && <> · per {indietro.senzaCifra === 1 ? 'uno' : indietro.senzaCifra} manca la cifra</>}
        </>}
      />
    );
  }
  const prossima = s.gruppi.flatMap((g) => (g.key === 'indietro' ? [] : g.righe))[0];
  if (prossima) {
    return (
      <MHero
        label="Prossimo versamento al fisco"
        unit={prossima.importo != null ? '€' : undefined}
        value={prossima.importo != null ? fmtEur(prossima.importo) : 'Manca la cifra'}
        sub={`${prossima.nome}, ${giornoBreve(prossima.data)} (${prossima.quando})`}
      />
    );
  }
  return <MHero label="Tasse da pagare" value="Niente" sub="nessun versamento nei prossimi 90 giorni" />;
}

// ════════════════════════════════════════════════════════════════════
// Le righe
// ════════════════════════════════════════════════════════════════════

const aperta = (r: RigaFisco) => r.stato !== 'PAGATA' && r.stato !== 'NEL_PIANO';

/** L'importo a destra; senza cifra la riga lo dice sotto il nome, e a destra non c'è niente. */
function importo(r: RigaFisco): ReactNode {
  return r.importo != null ? euroRiga(r.importo) : undefined;
}

/** Quello che si aggiunge alla riga di dettaglio: la cifra che manca, il pagamento forse trovato in banca. */
function inPiu(r: RigaFisco): ReactNode {
  if (!aperta(r)) return null;
  if (r.importo == null) return <> · manca la cifra</>;
  if (r.possibile) return <> · <span className="is-ink">forse già pagata</span></>;
  return null;
}

/**
 * Una tassa del calendario: il giorno a sinistra, il nome, quando e la rata.
 * Le non versate hanno l'icona rossa al posto del giorno: è passato, conta da
 * quanto. Il rosso sta lì e basta.
 */
function RigaCalendario({ r, onApri }: { r: RigaFisco; onApri: (r: RigaFisco) => void }) {
  const nonVersata = r.stato === 'NON_VERSATA';
  const giorno = r.data ? String(Number(r.data.slice(8, 10))) : '·';
  return (
    <MRow
      icon={nonVersata ? 'calendarClock' : undefined}
      text={nonVersata ? undefined : giorno}
      tone={nonVersata ? 'alert' : undefined}
      title={<DueRighe>{r.nome}</DueRighe>}
      sub={<>{r.quando}{r.rata ? ` · rata ${r.rata.n} di ${r.rata.di}` : ''}{inPiu(r)}</>}
      trail={importo(r)}
      trailSub={r.importo != null && aperta(r) ? provvisoria(r) : undefined}
      onClick={() => onApri(r)}
    />
  );
}

/** Una tassa di una vista per tipo: la data detta per intero, lo stato se non è "da pagare". */
function RigaTassa({ r, onApri }: { r: RigaFisco; onApri: (r: RigaFisco) => void }) {
  const pagata = r.stato === 'PAGATA';
  const quando = pagata ? `pagata il ${giornoBreve(r.pagataIl)}` : r.stato === 'NEL_PIANO' ? 'dentro il piano a rate' : `${giornoBreve(r.data)} · ${r.quando}`;
  return (
    <MRow
      icon={r.stato === 'NON_VERSATA' ? 'calendarClock' : pagata ? 'check' : 'calendar'}
      tone={r.stato === 'NON_VERSATA' ? 'alert' : undefined}
      title={<DueRighe>{r.nome}</DueRighe>}
      sub={<>{quando}{inPiu(r)}</>}
      trail={importo(r)}
      trailSub={r.importo != null && aperta(r) ? provvisoria(r) : undefined}
      onClick={() => onApri(r)}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// Calendario
// ════════════════════════════════════════════════════════════════════

function Calendario({ s, onApri, onVai }: { s: Sintesi | undefined; onApri: (r: RigaFisco) => void; onVai: (g: TaxGroup) => void }) {
  const navigate = useNavigate();
  if (!s) return <MGroup title="Calendario"><MEmpty>Carico il calendario delle tasse…</MEmpty></MGroup>;
  const indietro = s.gruppi.find((g) => g.key === 'indietro')?.righe ?? [];
  const avanti = s.gruppi.filter((g) => g.key !== 'indietro').flatMap((g) => g.righe)
    .sort((a, b) => (a.data ?? '').localeCompare(b.data ?? ''));
  const mesi = aGruppi(avanti, (r) => (r.data ?? '').slice(0, 7));
  const p = s.pagate;
  const banca = adminHref('banca', { vista: 'movimenti', side: 'DEBIT', unmatched: '1', q: 'Agenzia delle Entrate', from: `${s.anno}-01-01` });

  return (
    <>
      {indietro.length > 0 && (
        <MGroup title="Non versate in tempo" count={indietro.length}>
          {indietro.map((r) => <RigaCalendario key={r.key} r={r} onApri={onApri} />)}
        </MGroup>
      )}

      {mesi.length === 0 ? (
        <MGroup title="Prossimi 90 giorni"><MEmpty>Nessun versamento nei prossimi 90 giorni.</MEmpty></MGroup>
      ) : mesi.map((m, i) => (
        <MGroup key={m.chiave} title={meseGruppo(m.chiave)} count={m.items.length}
          note={i === mesi.length - 1 && s.piuAvanti.testo ? `Poi ${s.piuAvanti.testo}: li trovi nelle viste per tipo.` : undefined}>
          {m.items.map((r) => <RigaCalendario key={r.key} r={r} onApri={onApri} />)}
        </MGroup>
      ))}

      <MGroup title={p.label} note="Le tasse nuove si aggiungono dal computer: servono i documenti del commercialista.">
        {p.perGruppo.length === 0 ? <MEmpty>Nessuna tassa pagata nel {s.anno}, con i dati di oggi.</MEmpty>
          : p.perGruppo.map((g) => (
            <MRow key={g.group} icon="check" title={g.label} sub={plurale(g.n, 'versamento', 'versamenti')} trail={euro(g.totale)} onClick={() => onVai(g.group)} />
          ))}
        {p.daClassificare.length > 0 && (
          <MRow icon="landmark" title="Pagamenti al fisco senza tassa collegata"
            sub={`${plurale(p.daClassificare.length, 'pagamento', 'pagamenti')} in banca, fuori dal totale`}
            onClick={() => navigate(banca)} />
        )}
      </MGroup>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Le viste per tipo
// ════════════════════════════════════════════════════════════════════

const NOMI: Record<TaxGroup, { aperte: string; vuoto: string }> = {
  F24_DIPENDENTI: { aperte: 'F24 da pagare', vuoto: 'Nessun F24 dei dipendenti da pagare, con i dati di oggi.' },
  IVA: { aperte: 'IVA da versare', vuoto: 'Nessun trimestre IVA a sistema.' },
  SOCIETA: { aperte: 'Imposte da pagare', vuoto: 'Nessuna imposta della società da pagare, con i dati di oggi.' },
  RATE: { aperte: 'Altri debiti, fuori dai piani', vuoto: '' },
  ALTRI: { aperte: 'Versamenti da fare', vuoto: 'Nessun altro versamento al fisco da fare, con i dati di oggi.' },
};

/** Due o tre numeri della vista, come righe: l'etichetta, cosa vuol dire, il valore. */
function InBreve({ voci, extra, note }: { voci: VoceFascia[]; extra?: ReactNode; note?: ReactNode }) {
  if (!voci.length) return null;
  return (
    <MGroup title="In breve" note={note}>
      {voci.map((v) => (
        <MRow
          key={v.key}
          title={v.label}
          sub={v.sotto ? (v.tono === 'alert' ? <span className="is-alert">{v.sotto}</span> : v.sotto) : undefined}
          subWrap
          trail={v.valore != null ? euro(v.valore) : <span className={/manca/i.test(v.testo ?? '') ? 'amk-manca' : undefined}>{v.testo}</span>}
        />
      ))}
      {extra}
    </MGroup>
  );
}

function Pagate({ righe, titolo, onApri }: { righe: RigaFisco[]; titolo: string; onApri: (r: RigaFisco) => void }) {
  const [tutte, setTutte] = useState(false);
  if (!righe.length) return null;
  return (
    <MGroup title={titolo} count={righe.length}>
      {(tutte ? righe : righe.slice(0, PRIME)).map((r) => <RigaTassa key={r.key} r={r} onApri={onApri} />)}
      {righe.length > PRIME && <MMore label={tutte ? 'Mostra meno' : `Vedi tutte le ${righe.length}`} onClick={() => setTutte((v) => !v)} />}
    </MGroup>
  );
}

function VistaTipo({ group, canWrite, onApri }: { group: TaxGroup; canWrite: boolean; onApri: (r: RigaFisco) => void }) {
  const q = useQuery(fiscoGruppoQuery(group));
  const d = q.data;
  if (q.isLoading && !d) return <MGroup title={TAX_GROUP_LABEL[group]}><MEmpty>Carico le tasse…</MEmpty></MGroup>;
  if (!d) return <MGroup title={TAX_GROUP_LABEL[group]}><MEmpty>Non riesco a leggere le tasse in questo momento: riprova tra poco.</MEmpty></MGroup>;
  if (group === 'IVA') return <VistaIva d={d} canWrite={canWrite} onApri={onApri} />;
  if (group === 'RATE') return <VistaRate d={d} onApri={onApri} />;
  return (
    <>
      <InBreve voci={d.fascia} />
      <MGroup title={NOMI[group].aperte} count={d.righe.length || undefined}>
        {d.righe.length === 0 ? <MEmpty>{NOMI[group].vuoto}</MEmpty> : d.righe.map((r) => <RigaTassa key={r.key} r={r} onApri={onApri} />)}
      </MGroup>
      <Pagate righe={d.pagate} titolo="Già pagate" onApri={onApri} />
    </>
  );
}

function VistaIva({ d, canWrite, onApri }: { d: VistaGruppo; canWrite: boolean; onApri: (r: RigaFisco) => void }) {
  const openModal = useModalStore((s) => s.open);
  if (!d.iva) return <MGroup title="IVA"><MEmpty>Non riesco a leggere l'IVA in questo momento: riprova tra poco.</MEmpty></MGroup>;
  const { anni, notaAnticipi, anticipo } = d.iva;
  const senzaCifra = !anticipo.tax || anticipo.tax.amountDue <= 0;
  // L'anticipo di dicembre: se manca la riga la si crea già con la cifra, se c'è senza cifra la si completa (come sul computer).
  const inserisci = () => openModal('newTax', anticipo.tax
    ? { tax: anticipo.tax, prefill: { intent: 'cifra', source: 'CONFERMATO' } }
    : {
      prefill: {
        intent: 'cifra', kind: 'IVA_TRIMESTRALE', year: anticipo.anno, period: `${anticipo.anno}-acconto`,
        description: `Anticipo IVA di dicembre ${anticipo.anno}`, dueDate: anticipo.data, source: 'CONFERMATO',
      },
    });
  return (
    <>
      <InBreve
        voci={d.fascia}
        note={notaAnticipi ?? undefined}
        extra={canWrite && senzaCifra ? <Gesto icon="pencil" label="Inserisci la cifra dell'anticipo" sub="la dà il commercialista" onClick={inserisci} /> : undefined}
      />
      {[...anni].reverse().map((a) => (
        <MGroup key={a.anno} title={`IVA del ${a.anno}`} count={a.righe.length}
          note={a.resta != null && a.resta > 0 ? `Non versati ${euroEsatto(a.resta)}.` : undefined}>
          {a.righe.map((r) => <RigaTassa key={r.key} r={r} onApri={onApri} />)}
        </MGroup>
      ))}
      {anni.length === 0 && <MGroup title="IVA"><MEmpty>Nessun trimestre IVA a sistema.</MEmpty></MGroup>}
    </>
  );
}

function VistaRate({ d, onApri }: { d: VistaGruppo; onApri: (r: RigaFisco) => void }) {
  const [foglio, setFoglio] = useState<null | { kind: 'dentro' | 'rate'; p: PianoRate } | { kind: 'serie' }>(null);
  const piani = d.piani ?? [];
  const serie = d.serieChiuse ?? [];
  return (
    <>
      {/* Con un piano, i suoi numeri li dice già il gruppo del piano: "in breve" sarebbe un doppione. */}
      {piani.length === 0 && <InBreve voci={d.fascia} />}
      {piani.map((p) => (
        <MGroup key={p.obligationId} title={`Piano a rate${p.numero ? ` n. ${p.numero}` : ''}`}>
          {p.prossima && (
            <MRow icon="calendar" title={`Prossima rata, la ${p.prossima.n}ª`} sub={`${p.prossima.dataTesto}, ${p.prossima.quando}`} trail={p.rataMese != null ? euroRiga(p.rataMese) : undefined} />
          )}
          <MRow icon="check" title="Rate pagate" sub={`${euro(p.versato)} versati finora`} trail={`${p.pagate} di ${p.rate}`}
            onClick={() => setFoglio({ kind: 'rate', p })} />
          <MRow icon="coins" title="Resta da pagare" sub={p.ultimaTesto ? `l'ultima rata ${p.ultimaTesto}` : undefined} trail={euro(p.resta)} />
          {p.dentro.length > 0 && <MRow icon="list" title="Cosa c'è dentro il piano" sub={plurale(p.dentro.length, 'debito', 'debiti')} onClick={() => setFoglio({ kind: 'dentro', p })} />}
        </MGroup>
      ))}
      {d.righe.length > 0 && (
        <MGroup title={NOMI.RATE.aperte} count={d.righe.length}>
          {d.righe.map((r) => <RigaTassa key={r.key} r={r} onApri={onApri} />)}
        </MGroup>
      )}
      <Pagate righe={d.pagate} titolo="Altri debiti già pagati" onApri={onApri} />
      {serie.length > 0 && (
        <MGroup title="Vecchi piani già finiti">
          <MRow icon="clock" title="Trovati in banca" sub={plurale(serie.length, 'serie di pagamenti', 'serie di pagamenti')} onClick={() => setFoglio({ kind: 'serie' })} />
        </MGroup>
      )}

      {foglio?.kind === 'dentro' && (
        <Foglio open onClose={() => setFoglio(null)} title="Cosa c'è dentro il piano">
          <div className="mk-list">
            {foglio.p.dentro.map((x) => <MRow key={x.cosa} title={x.cosa} trail={euroRiga(x.importo)} />)}
          </div>
          <div className="mk-group-note">In tutto {euroEsatto(foglio.p.dentro.reduce((n, x) => n + x.importo, 0))}, interessi compresi.</div>
        </Foglio>
      )}
      {foglio?.kind === 'rate' && <FoglioRate p={foglio.p} onApri={(r) => { setFoglio(null); onApri(r); }} onClose={() => setFoglio(null)} />}
      {foglio?.kind === 'serie' && (
        <Foglio open onClose={() => setFoglio(null)} title="Vecchi piani già finiti">
          <div className="mk-list">
            <Testo>Serie di pagamenti uguali, uno al mese, che in banca risultano finite: probabilmente piani a rate già chiusi. Non sono collegati a nessuna tassa.</Testo>
          </div>
          <div className="mk-list">
            {serie.map((x) => <MRow key={x.key} title={`${x.n} ${x.tipo}`} sub={x.testo} subWrap trail={euroRiga(x.importo)} trailSub="l'uno" />)}
          </div>
        </Foglio>
      )}
    </>
  );
}

function FoglioRate({ p, onApri, onClose }: { p: PianoRate; onApri: (r: RigaFisco) => void; onClose: () => void }) {
  const uguale = (r: RigaFisco) => p.rataMese != null && r.importo === p.rataMese;
  return (
    <Foglio open onClose={onClose} title={`Le ${p.rate} rate del piano`}>
      {p.ratePagate.length > 0 && (
        <MGroup title="Già pagate" count={p.ratePagate.length}>
          {p.ratePagate.map((r) => (
            <MRow key={r.key} icon="check" title={r.nome} sub={`pagata il ${giornoBreve(r.pagataIl)}`}
              trail={uguale(r) ? <span className="amk-manca">la rata</span> : importo(r)} onClick={() => onApri(r)} />
          ))}
        </MGroup>
      )}
      {(p.rateDiverse.length > 0 || p.rateUguali) && (
        <MGroup title="Da pagare" count={p.rate - p.pagate}
          note={p.rataMese != null ? `"La rata" vale ${euroEsatto(p.rataMese)}, ogni mese.` : undefined}>
          {p.rateDiverse.map((r) => <RigaTassa key={r.key} r={r} onApri={onApri} />)}
          {p.rateUguali && (
            <MRow icon="repeat" title={`Rate da ${p.rateUguali.daN} a ${p.rateUguali.aN}`}
              sub={`${plurale(p.rateUguali.n, 'rata uguale', 'rate uguali')}, dal ${p.rateUguali.primaTesto} al ${p.rateUguali.ultimaTesto}`} subWrap
              trail={<span className="amk-manca">la rata</span>} />
          )}
        </MGroup>
      )}
    </Foglio>
  );
}

// ════════════════════════════════════════════════════════════════════
// Il pannello di una tassa
// ════════════════════════════════════════════════════════════════════

function FoglioTassa({ r, canWrite, onClose, onVai, onPaga }: {
  r: RigaFisco; canWrite: boolean; onClose: () => void; onVai: (g: TaxGroup) => void; onPaga: (r: RigaFisco) => void;
}) {
  const openModal = useModalStore((s) => s.open);
  const { remove } = useAdminActions();
  const conferma = useConfermaPossibile();
  const t = r.tax;
  const apertaR = aperta(r);
  const inserisci = () => { if (!t) return; onClose(); openModal('newTax', { tax: t, prefill: { intent: 'cifra', source: 'CONFERMATO' } }); };

  // Il gesto principale, nello stesso ordine del computer: il pagamento trovato in
  // banca si conferma; senza cifra la si inserisce; altrimenti si segna pagata.
  let primary: ReactNode;
  if (canWrite && apertaR && r.possibile && r.installmentId) {
    primary = (
      <button className="btn btn-primary m-block" disabled={conferma.isPending} onClick={() => { onClose(); conferma.mutate(r); }}>
        <Icon name="check" size={12} /> Conferma il pagamento
      </button>
    );
  } else if (canWrite && apertaR && r.importo == null && t) {
    primary = <button className="btn btn-primary m-block" onClick={inserisci}><Icon name="pencil" size={12} /> Inserisci la cifra</button>;
  } else if (canWrite && apertaR && r.importo != null && r.installmentId) {
    primary = <button className="btn btn-primary m-block" onClick={() => onPaga(r)}><Icon name="check" size={12} /> Segna pagata</button>;
  }

  return (
    <Foglio open onClose={onClose} title={r.nome} primary={primary}>
      <div className="mk-list">
        {r.stato === 'PAGATA'
          ? <Fatto label="Pagata il" value={giornoBreve(r.pagataIl) || '—'} />
          : <Fatto label="Scadenza" alert={r.stato === 'NON_VERSATA'} value={<>{giornoBreve(r.data) || '—'}{r.quando && <span className="s">{r.quando}</span>}</>} />}
        <Fatto label={r.stato === 'PAGATA' ? 'Versato' : 'Da versare'} value={r.importo != null ? euroEsatto(r.importo) : 'manca la cifra'} />
        <Fatto label="Stato" value={STATO_LABEL[r.stato]} />
        {r.rata && <Fatto label="Rata" value={`${r.rata.n} di ${r.rata.di}`} />}
        {r.stato !== 'NEL_PIANO' && <Fatto label="Da dove viene la cifra" value={TAX_SOURCE_LABEL[r.fonte] ?? r.fonte} />}
        {r.diAndrea && <Fatto label="Di chi" value="Di Andrea, non della società" />}
      </div>

      {(r.possibile && apertaR) || r.fonteSpiegazione || r.dettaglio ? (
        <div className="mk-list">
          {r.possibile && apertaR && (
            <Testo>
              In banca: {r.possibile.testo}, <strong>{euroEsatto(r.possibile.totale)}</strong> in tutto
              {r.possibile.differenza > 0.005 ? ', con il ravvedimento probabilmente' : ''}. Confermandolo, la tassa risulta pagata con quei movimenti.
            </Testo>
          )}
          {r.fonteSpiegazione && <Testo>{r.fonteSpiegazione}</Testo>}
          {!r.possibile && !r.fonteSpiegazione && r.dettaglio && <Testo>{r.dettaglio}</Testo>}
        </div>
      ) : null}

      <div className="mk-list">
        {r.stato === 'NEL_PIANO' && <Gesto icon="list" label="Vedi il piano a rate" onClick={() => onVai('RATE')} />}
        {r.vai && r.stato !== 'NEL_PIANO' && (
          <Gesto icon="calendar" label={r.vai === 'IVA' ? "Apri l'IVA trimestre per trimestre" : `Vedi in ${TAX_GROUP_LABEL[r.vai]}`} onClick={() => onVai(r.vai!)} />
        )}
        {canWrite && t && apertaR && r.possibile && r.importo != null && r.installmentId && (
          <Gesto icon="check" label="Segna pagata a mano" onClick={() => onPaga(r)} />
        )}
        {canWrite && t && apertaR && r.inserisciCifra && r.importo != null && (
          <Gesto icon="pencil" label="Inserisci la cifra del commercialista" onClick={inserisci} />
        )}
        {canWrite && t && <Gesto icon="pencil" label="Modifica la tassa" onClick={() => { onClose(); openModal('newTax', { tax: t }); }} />}
        {canWrite && t && (
          <Gesto icon="trash" label="Elimina la tassa" danger onClick={() => {
            onClose();
            remove.mutate({
              path: `/admin/taxes/${t.id}`,
              title: 'Eliminare questa tassa?',
              body: t.rate > 1 ? `${r.nome}, con tutte le sue ${t.rate} rate.` : r.nome,
              okTitle: 'Tassa eliminata',
            });
          }} />
        )}
      </div>
    </Foglio>
  );
}
